import { Grid, Typography } from '@mui/material';
import { AddItemButton } from '@tassat/button';
import { ActionTypes, doesWorkflowRejectCodeExist } from '@tassat/common-definitions';
import { WalletUserMappingForm } from '@tassat/forms';
import { UserAccountMapped } from '@tassat/fred-extended-definitions';
import { FormModal } from '@tassat/modal';
import {
    ConfirmationWorkflowAction,
    ConfirmationWorkflowConsumer,
    ConfirmationWorkflowContext,
    ConfirmationWorkflowReducer,
    DEFAULT_INITIAL_STATE
} from '@tassat/workflow';
import React from 'react';

import { submitWalletUserMappingForm } from 'Containers/admin/forms/helpers/walletUserMappingForm';
import Worker from 'Lib/worker/PrimusWorkerWrapper';
import { useAppSelector } from 'Reducers/store/hooks';
import { getCurrentRole } from 'Selectors/common';
import { getMappedWalletUserAccountsByAccountId, getUnmappedUsersByAccountId } from 'Selectors/userAccounts';
import { useFeatures } from 'SharedComponents/hooks/useFeatures';
import { ClientRoleEnum } from 'SharedComponents/users/ClientRoleEnum';

import FirmWalletUsersAgGrid from './firmWalletUsersGrid';

import type { FirmWalletUsersProps } from '.';

export function FirmWalletUsers(props: FirmWalletUsersProps) {
    const {
        classes,
        firm,
        wallet
    } = props;

    const userRole = useAppSelector(getCurrentRole);
    const accountId = props.match.params.accountId;
    const userAccounts = useAppSelector((state) => getMappedWalletUserAccountsByAccountId(state, '', accountId, true));
    const users = useAppSelector((state) => getUnmappedUsersByAccountId(state, '', accountId, '', props.firm?.FirmId));
    const [features] = useFeatures();
    const [ mapUsersFormOpen, setMapUsersFormOpen ] = React.useState<boolean>(false);
    const closeMapUsersForm = () => setMapUsersFormOpen(false);
    const [ confirmationWorkflowState, dispatchConfirmationWorkflowAction ] =
    React.useReducer(ConfirmationWorkflowReducer, DEFAULT_INITIAL_STATE);
    const confirmationContext = React.useMemo(() => {
        return {
            confirmationWorkflowState,
            dispatchConfirmationWorkflowAction
        };
    }, [ confirmationWorkflowState, dispatchConfirmationWorkflowAction ]);

    const unmapUserFromAccountValidation = async (userAccountMapped: UserAccountMapped) => {
        const user = userAccountMapped.user;
        const account = userAccountMapped.account;

        const validationSuccessfulAction: ConfirmationWorkflowAction = {
            type: 'VALIDATION_SUCCESS',
            workflowConfiguration: {
                selectedItem: userAccountMapped,
                actionType: ActionTypes.unmap,
                confirmTitle: `Confirm Unmapping ${user.Email}`,
                confirmBody: `Please confirm removing permissions to account ${account.AccountName} for user ${user.Email}.`
            }
        };
        dispatchConfirmationWorkflowAction(validationSuccessfulAction);

        return false;
    };

    const unmapUserFromAccount = async (userAccountMapped: UserAccountMapped) => {
        const userAccount = userAccountMapped.userAccount;
        const user = userAccountMapped.user;
        const account = userAccountMapped.account;

        try {
            const response = await Worker.getInstance()
                .updateUserAccount({
                    UserId: user.UserId,
                    AccountId: account.AccountId,
                    ClientRoleId: ClientRoleEnum.NOT_ASSIGNED,
                    WireRoleId: userAccount.WireRoleId,
                    UserAccountId: userAccount.UserAccountId,
                    IsFavorite: userAccount.IsFavorite,
                    IsDeleted: userAccount.IsDeleted
                });

            if (response.data && response.data.Status === 48) {
                const serverSuccessAction: ConfirmationWorkflowAction = {
                    type: 'SERVER_SUCCESS',
                    workflowConfiguration: {
                        successTitle: `Successfully Unmapped User ${user.Email}`,
                        successBody: `Successfully unmapped the user ${user.Email} from account ${account.AccountName}.`
                    }
                };
                dispatchConfirmationWorkflowAction(serverSuccessAction);
            } else if (response.data && doesWorkflowRejectCodeExist(response.data.Status)) {
                const serverErrorAction: ConfirmationWorkflowAction = {
                    type: 'SERVER_ERROR',
                    workflowConfiguration: {
                        failureTitle: `Failed to Unmap User ${user.Email}`,
                        failureBody: `Could not unmap the user ${user.Email} from account ${account.AccountName}.`
                    }
                };
                dispatchConfirmationWorkflowAction(serverErrorAction);
            }
        } catch (error) {
            const serverErrorAction: ConfirmationWorkflowAction = {
                type: 'SERVER_ERROR',
                workflowConfiguration: {
                    failureTitle: `Failed to Unmap User ${user.Email}`,
                    failureBody: `Something went wrong unmapping the user ${user.Email} from account ${account.AccountName}.`
                }
            };
            dispatchConfirmationWorkflowAction(serverErrorAction);
        }
    };

    React.useEffect(() => {
        //set initial confirmation settings
        const configureConfirmationWorkflowAction: ConfirmationWorkflowAction = {
            type: 'CONFIGURE_WORKFLOW',
            workflowConfiguration: {
                setSelectedItem: () => userAccounts,
                preActionValidationAndConfig: { [ActionTypes.unmap]: unmapUserFromAccountValidation },
                onConfirm: { [ActionTypes.unmap]: unmapUserFromAccount }
            }
        };
        dispatchConfirmationWorkflowAction(configureConfirmationWorkflowAction);
    }, [userAccounts]);

    return (
        <ConfirmationWorkflowContext.Provider value={confirmationContext}>
            <Grid
                className={classes.root}
                container
                direction='column'
                spacing={0}
                wrap='nowrap'
            >
                <Grid
                    className={classes.headerContainer}
                    container
                    direction='row'
                    justifyContent='space-between'
                    spacing={0}
                    wrap='nowrap'
                >
                    <Grid
                        container
                        direction='column'
                        item
                        spacing={0}
                        wrap='nowrap'
                        xs={6}
                    >
                        <Typography
                            className={classes.panelHeader}
                            variant='h6'
                        >Associated Users
                        </Typography>
                    </Grid>
                    {(!firm?.IsClosed && !wallet.IsClosed && userRole.IsManageFirmAllowed) &&
                        <Grid
                            className={classes.mapUserButtonContainer}
                            item
                        >
                            <AddItemButton
                                onClick={() => setMapUsersFormOpen(true)}
                                text='Map User(s)'
                            />
                        </Grid>}
                </Grid>
                <Grid
                    item
                    xs={12}
                >
                    <FirmWalletUsersAgGrid
                        firm={firm}
                        userAccounts={userAccounts}
                        userRole={userRole}
                        wallet={wallet}
                    />
                </Grid>
                <FormModal
                    Form={WalletUserMappingForm}
                    formProps={{
                        account: wallet,
                        users: users,
                        features,
                        submitWalletUserMappingForm: submitWalletUserMappingForm
                    }}
                    onClose={closeMapUsersForm}
                    open={mapUsersFormOpen}
                    title='Map User(s)'
                />
                <ConfirmationWorkflowConsumer />
            </Grid>
        </ConfirmationWorkflowContext.Provider>
    );
}